import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import AxiosInstance, { TokenService } from "../../../services/Api";
import "./Index.css";

// ─── Single Birthday Slide ───
const BirthdaySlide = ({ person, t }) => {
  return (
    <div className="celebration-card celebration-birthday">
      {person ? (
        <div className="celebration-slide-content">
          <div className="celebration-slide-avatar">
            <div className="celebration-avatar-circle birthday-avatar-bg">
              <span>🎂</span>
            </div>
          </div>
          <div className="celebration-slide-info">
            <h2 className="celebration-title">
              {t.birthdayTitle || "Birthday!"} 🥰🎂🎊
            </h2>
            <h3 className="celebration-person-name">{person.name}</h3>
            <p className="celebration-desc">
              {person.message ||
                t.birthdayDescription ||
                "Wishing you a wonderful birthday! 🎉"}
            </p>
          </div>
        </div>
      ) : (
        <>
          <h2 className="celebration-title">
            {t.birthdayTitle || "Birthday!"} 🥰🎂🎊
          </h2>
          <p className="celebration-desc">No Records</p>
        </>
      )}
    </div>
  );
};

// ─── Single Anniversary Slide ───
const AnniversarySlide = ({ person, t }) => {
  return (
    <div className="celebration-card celebration-anniversary">
      {person ? (
        <div className="celebration-slide-content">
          <div className="celebration-slide-avatar">
            <div className="celebration-avatar-circle anniversary-avatar-bg">
              <span>🎊</span>
            </div>
          </div>
          <div className="celebration-slide-info">
            <h2 className="celebration-title">
              {t.workAnniversaryTitle || "Work Anniversary!"} 🎊👏🎉
            </h2>
            <h3 className="celebration-person-name">{person.name}</h3>
            <p className="celebration-desc">
              {person.message ||
                t.workAnniversaryDescription ||
                "Congratulations on your work anniversary! 🎉"}
            </p>
          </div>
        </div>
      ) : (
        <>
          <h2 className="celebration-title">
            {t.workAnniversaryTitle || "Work Anniversary!"} 🎊👏🎉
          </h2>
          <p className="celebration-desc">No Records</p>
        </>
      )}
    </div>
  );
};

// ─── Main Component ───
export default function CelebrationBanner({ birthdays }) {
  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations]);

  const [anniversaries, setAnniversaries] = useState([]);
  const [birthdayIndex, setBirthdayIndex] = useState(0);
  const [anniversaryIndex, setAnniversaryIndex] = useState(0);
  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";

  // ─── Anniversaries ───
  const fetchAnniversaries = useCallback(async () => {
    if (!school_id) return; // ✅ prevent empty call

    const data = {
      school_id: parseInt(school_id), // ✅ added
      date: moment().format("YYYY-MM-DD"),
    };

    try {
      const res = await AxiosInstance.post("school/anniversary", data);
      if (res.data?.status) {
        setAnniversaries(res.data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch anniversaries", error);
    }
  }, [school_id]); // ✅ IMPORTANT

  useEffect(() => {
    fetchAnniversaries();
  }, [fetchAnniversaries]);

  // ─── Birthday Slider (every 5 seconds) ───
  useEffect(() => {
    if (!birthdays || birthdays.length <= 1) return;
    const interval = setInterval(() => {
      setBirthdayIndex((prev) =>
        prev === birthdays.length - 1 ? 0 : prev + 1,
      );
    }, 5000);
    return () => clearInterval(interval);
  }, [birthdays]);

  // ─── Anniversary Slider (every 5 seconds) ───
  useEffect(() => {
    if (!anniversaries || anniversaries.length <= 1) return;
    const interval = setInterval(() => {
      setAnniversaryIndex((prev) =>
        prev === anniversaries.length - 1 ? 0 : prev + 1,
      );
    }, 5000);
    return () => clearInterval(interval);
  }, [anniversaries]);

  const hasBirthdays = birthdays && birthdays.length > 0;
  const hasAnniversaries = anniversaries && anniversaries.length > 0;
  const currentBirthday = hasBirthdays ? birthdays[birthdayIndex] : null;
  const currentAnniversary = hasAnniversaries
    ? anniversaries[anniversaryIndex]
    : null;

  return (
    <div className="celebration-container">
      {/* Birthday Slider */}
      <div className="celebration-slider-wrapper">
        <BirthdaySlide person={currentBirthday} t={t} />
        {hasBirthdays && birthdays.length > 1 && (
          <div className="celebration-dots">
            {birthdays.map((_, i) => (
              <span
                key={i}
                className={`celebration-dot ${i === birthdayIndex ? "active" : ""}`}
                onClick={() => setBirthdayIndex(i)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Anniversary Slider */}
      <div className="celebration-slider-wrapper">
        <AnniversarySlide person={currentAnniversary} t={t} />
        {hasAnniversaries && anniversaries.length > 1 && (
          <div className="celebration-dots">
            {anniversaries.map((_, i) => (
              <span
                key={i}
                className={`celebration-dot ${i === anniversaryIndex ? "active" : ""}`}
                onClick={() => setAnniversaryIndex(i)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
